import type { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import type { FC } from 'react'

import LoginModalButton from '@components/common/LoginModalButton'
import PublicNavbar from '@components/common/PublicNavbar'
import CommunitySiteView from '@components/project/CommunitySiteView/CommunitySiteView'
import { ERROR_CODE_PAGE_NOT_FOUND } from '@motif/errors'
import { getOrigin } from '@motif/shared/domain.edge'
import motifFetch from '@motif/shared/fetcher'

const Modals = dynamic(() => import('@components/common/Layout/Modals'), {
  ssr: false,
})

type CommunitySiteProps = {
  domain: string
  displayName: string
  duplicationCount: number
}

interface SiteProps {
  data: CommunitySiteProps
}

const Site: FC<SiteProps> = ({ data }) => {
  return (
    <>
      <NextSeo title={`${data?.displayName || 'Untitled site'} – Sites`} />
      <div className="flex h-full flex-col">
        <div className="fixed z-20 w-full">
          <PublicNavbar title="Community" titleHref="/community" />
        </div>
        {data && (
          <div className="relative mt-[60px] flex-grow">
            <CommunitySiteView
              title={data?.displayName}
              domain={data?.domain}
              includeLink={{
                label: '← Back to sites',
                href: '/community/sites',
              }}
              AddButton={
                <LoginModalButton
                  label="Duplicate"
                  num={Math.max(1, data?.duplicationCount || 0)}
                  title="Create a new project from this site"
                  subtitle="Sign up to get started building with blocks, text and code."
                />
              }
            />
          </div>
        )}
        <Modals />
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const projectId: string = (params?.id || '') as string
    const response = await motifFetch({
      url: `${getOrigin()}/api/community/sites/${projectId}`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return {
      props: { data: response.data || null },
      revalidate: 5,
    }
  } catch {
    // Do nothing
  }

  return {
    props: {
      data: { code: ERROR_CODE_PAGE_NOT_FOUND },
    },
    revalidate: 20,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: true }
}

export default Site
